/**
 * API Service - Facade/Barrel Export
 * 
 * This file serves as a facade, providing backward compatibility
 * while delegating to specialized modules.
 * 
 * Follows Facade Pattern and Dependency Inversion Principle.
 * 
 * Architecture:
 * - Extractors: Extract data from Office.js items
 * - Services: Handle API operations and business logic
 * - Utils: Shared utilities for date, exchange, fetch operations
 */

// ============================
// EXTRACTORS
// ============================
import { EmailExtractor } from './extractors/email-extractor.js';
import { AppointmentExtractor } from './extractors/appointment-extractor.js';
import { AttachmentExtractor } from './extractors/attachment-extractor.js';

// ============================
// DOMAIN SERVICES
// ============================
import { ContactService } from './domain/contact-service.js';
import { LeadService } from './domain/lead-service.js';
import { ActivityService } from './domain/activity-service.js';
import { AttachmentService } from './domain/attachment-service.js';
import { UserService } from './domain/user-service.js';
import { AIAssistantService } from './domain/ai-assistant-service.js';

// ============================
// UTILITIES
// ============================
import { DateUtils } from './utils/date-utils.js';
import { ExchangeUtils } from './utils/exchange-utils.js';

// ============================
// LEGACY API EXPORTS
// ============================
// These exports maintain backward compatibility with existing code
// All functionality is delegated to the appropriate service/extractor

// Utility exports
export const toISOString = DateUtils.toISOString;
export const detectExchangeCapabilities = ExchangeUtils.detectCapabilities;

// Contact service exports
export const fetchContactsByEmail = ContactService.fetchByEmails.bind(ContactService);
export const searchContacts = ContactService.searchContacts.bind(ContactService);
export const extractContactData = ContactService.extractContactData.bind(ContactService);

// Lead service exports
export const fetchLeadsByContact = LeadService.fetchByContact.bind(LeadService);
export const fetchLeadsByContacts = LeadService.fetchByContacts.bind(LeadService);

// User service exports
export const fetchAllUsers = UserService.fetchAllUsers.bind(UserService);
export const searchUsers = UserService.searchUsers.bind(UserService);

// Activity service exports
export const linkEmailToContact = ActivityService.linkEmail.bind(ActivityService);
export const linkAppointmentToContact = ActivityService.linkAppointment.bind(ActivityService);

// Attachment service exports
export const extractEmailAttachments = AttachmentExtractor.extractAttachments.bind(AttachmentExtractor);
export const getAttachmentContent = AttachmentExtractor.getAttachmentContent.bind(AttachmentExtractor);
export const getS3UploadUrl = AttachmentService.getS3UploadUrl.bind(AttachmentService);
export const uploadToS3 = AttachmentService.uploadToS3.bind(AttachmentService);
export const linkAttachmentsToRecord = AttachmentService.linkAttachmentsToRecord.bind(AttachmentService);
export const processEmailAttachments = AttachmentService.processAttachments.bind(AttachmentService);

// Helper exports for consistent uniqueId generation
export const getUniqueEmailIdForItem = (emailItem) => EmailExtractor.getUniqueEmailId(emailItem);
export const getUniqueAppointmentIdForItem = (appointmentItem) => AppointmentExtractor.getUniqueAppointmentId(appointmentItem);

// ============================
// RE-EXPORTS FOR DIRECT ACCESS
// ============================
// Allow direct import of classes for advanced use cases
export { EmailExtractor } from './extractors/email-extractor.js';
export { AppointmentExtractor } from './extractors/appointment-extractor.js';
export { AttachmentExtractor } from './extractors/attachment-extractor.js';
export { ContactService } from './domain/contact-service.js';
export { LeadService } from './domain/lead-service.js';
export { ActivityService } from './domain/activity-service.js';
export { AttachmentService } from './domain/attachment-service.js';
export { UserService } from './domain/user-service.js';
export { AIAssistantService } from './domain/ai-assistant-service.js';
export { DateUtils } from './utils/date-utils.js';
export { ExchangeUtils } from './utils/exchange-utils.js';
export { FetchUtils } from './utils/fetch-utils.js';
export { Logger } from './utils/logger.js';
